import { range, sortBy, sumBy, uniq } from 'lodash';
import {
	ContributionFromEnum,
	ContributionStatusEnum,
	SingleUserContributionData
} from '../../api/users/interfaces/ContributionData';
import { PlanValueHistoryI, PlanValuePointI } from '../../api/users/getPlansValueHistory';
import { MAXIMO_CREIBLE, Rentabilidad, rentabilidadEntreSaldos } from './rentabilidad';
import { SENTIDO_DEL_FLUJO } from './movimientos';

/**
 * Las series de la gráfica «Evolución de tus planes» del Resumen multicuenta (chirigota#613,
 * product#6 Fase B). Los puntos son el valor de cada plan mes a mes (`GET /users/plans/value-history`:
 * lo que la sync lee de Caser para ESTA persona) y lo que entra y sale, sus movimientos.
 *
 * 🔴 Un mes sin punto se queda sin punto: nunca se rellena con 0, que pintaría que el plan se
 * vació. La línea lo cruza uniendo los meses de los lados (`conMesesSeguidos`).
 */

/** Un punto de la gráfica: en euros o en %, según la serie. */
export interface PuntoEvolucion {
	/** `AAAA-MM`. */
	mes: string;
	valor: number;
}

/** `'todo'` («Desde el inicio») o un año, `'AAAA'`. */
export type PeriodoEvolucion = string;

export const DESDE_EL_INICIO: PeriodoEvolucion = 'todo';

const MES_DE_MADRID = new Intl.DateTimeFormat('en-CA', {
	timeZone: 'Europe/Madrid',
	year: 'numeric',
	month: '2-digit'
});

/** `AAAA-MM` en España: el mismo mes que el de la card, que usa el año de Madrid. */
export const mesDeMadrid = (fecha: Date) => MES_DE_MADRID.format(fecha);

const delPeriodo = (puntos: PlanValuePointI[], periodo: PeriodoEvolucion) =>
	periodo === DESDE_EL_INICIO ? puntos : puntos.filter((p) => p.month.startsWith(`${periodo}-`));

const confirmados = (movimientos: SingleUserContributionData[]) =>
	movimientos.filter((m) => m.statusContribution === ContributionStatusEnum.CONFIRMED);

const dia = (m: SingleUserContributionData) => m.dateContribution.slice(0, 10);

/** € de cada mes. */
export const serieEnEuros = (
	puntos: PlanValuePointI[],
	periodo: PeriodoEvolucion
): PuntoEvolucion[] =>
	delPeriodo(puntos, periodo).map((p) => ({ mes: p.month, valor: p.vestedRightsCents / 100 }));

/**
 * El mes en curso es la cifra de la card (`actual`), que se calcula con el saldo de hoy en Caser y
 * no con la foto de anoche. Si aún no tiene punto (el día 1 la foto cierra el mes anterior) o salió
 * fuera de ±80 %, se añade con la de la card. Si la card no tiene dato (`null`), ese mes tampoco.
 * Sin `actual` (`undefined`, la card aún carga), se queda lo calculado.
 */
const conLaCard = (
	serie: PuntoEvolucion[],
	mesActual: string,
	actual: Rentabilidad | null | undefined
): PuntoEvolucion[] => {
	if (actual === undefined) return serie;
	const anteriores = serie.filter((p) => p.mes !== mesActual);
	return actual ? [...anteriores, { mes: mesActual, valor: actual.porcentaje }] : anteriores;
};

export interface EntradaSerieDelAnio {
	puntos: PlanValuePointI[];
	anio: string;
	movimientos: SingleUserContributionData[];
	/** Alta del plan (`createdAt`): un plan abierto ese año empezó el año a 0. */
	creadoEn: string;
	hoy: Date;
	/** La rentabilidad del año de la card (`rentabilidadAnio`): manda en el mes en curso. */
	actual?: Rentabilidad | null;
	/**
	 * El saldo (€) y el día de partida de la card (`vestedRightsAtYearStartCents` y
	 * `vestedRightsAtYearStartDate` de `GET /users/plans`). En el año en curso mandan sobre la foto
	 * de diciembre de la serie, para partir de lo mismo que la card: con varios productos, la card
	 * usa el día más antiguo y la serie el más reciente. `saldo: null` = no se sabe; `fecha: null` =
	 * se cuenta todo el año. Sin ellos (un comparsa anterior), la foto de diciembre.
	 */
	partidaDelAnioEnCurso?: { saldo: number | null; fecha: string | null };
}

/**
 * La rentabilidad del año `anio` hasta cada mes: la cuenta de la card (`rentabilidadEntreSaldos`),
 * con el valor de ese mes en vez del de hoy.
 *
 * - Parte del valor de diciembre del año anterior (el que cierra la foto del 1 de enero, como el
 *   saldo de partida de la card), y lo que entra y sale cuenta desde el día siguiente a esa foto
 *   hasta el de la foto del mes.
 * - Un plan abierto ese año parte de 0 y cuenta todo lo del año.
 * - 🔴 Un plan de antes sin el valor de diciembre no tiene serie ese año: no se sabe de dónde
 *   parte, y un 0 convertiría todo su saldo en ganancia.
 */
export function serieDelAnio(entrada: EntradaSerieDelAnio): PuntoEvolucion[] {
	const { anio, hoy, actual } = entrada;
	const serie = rentabilidadesDelAnio(entrada).flatMap(({ mes, rentabilidad }) =>
		rentabilidad ? [{ mes, valor: rentabilidad.porcentaje }] : []
	);
	const mesActual = mesDeMadrid(hoy);
	return mesActual.startsWith(`${anio}-`) ? conLaCard(serie, mesActual, actual) : serie;
}

/**
 * La rentabilidad del año hasta cada mes con foto de `anio` (`null` fuera de ±80 %), sin la card:
 * el núcleo de `serieDelAnio` y de `rentabilidadAlCierreDelAnio`.
 */
function rentabilidadesDelAnio({
	puntos,
	anio,
	movimientos,
	creadoEn,
	hoy,
	partidaDelAnioEnCurso
}: EntradaSerieDelAnio): { mes: string; rentabilidad: Rentabilidad | null }[] {
	const esElAnioEnCurso = mesDeMadrid(hoy).startsWith(`${anio}-`);
	const deDiciembre = puntos.find((p) => p.month === `${Number(anio) - 1}-12`);
	const partida =
		esElAnioEnCurso && partidaDelAnioEnCurso
			? partidaDelAnioEnCurso
			: deDiciembre
				? { saldo: deDiciembre.vestedRightsCents / 100, fecha: deDiciembre.date }
				: mesDeMadrid(new Date(creadoEn)).slice(0, 4) >= anio
					? { saldo: 0, fecha: null }
					: { saldo: null, fecha: null };
	if (partida.saldo === null) return [];
	const saldoInicial = partida.saldo;
	const desde = partida.fecha;
	const cuentaDesde = (m: SingleUserContributionData) =>
		desde ? dia(m) > desde : dia(m) >= `${anio}-01-01`;
	const validos = confirmados(movimientos).filter(cuentaDesde);

	return delPeriodo(puntos, anio).map((p) => ({
		mes: p.month,
		rentabilidad: rentabilidadEntreSaldos({
			saldo: p.vestedRightsCents / 100,
			saldoInicial,
			flujoNeto: sumBy(
				validos.filter((m) => dia(m) <= p.date),
				(m) => SENTIDO_DEL_FLUJO[m.typeContribution] * m.contributionAmount
			)
		})
	}));
}

/**
 * El valor (€) de un plan al cierre de un año pasado: el de su último mes con foto de ese año.
 * `null` si ese año no tiene ninguna (el plan aún no existía): nunca 0.
 */
export const valorAlCierreDelAnio = (puntos: PlanValuePointI[], anio: string): number | null => {
	const delAnio = delPeriodo(puntos, anio);
	return delAnio.length ? delAnio[delAnio.length - 1].vestedRightsCents / 100 : null;
};

/**
 * La rentabilidad de un año pasado al cierre: la de su último mes con foto, con ganancia y base
 * para poder sumar planes (`rentabilidadAgregada`). `null` sin dato o fuera de ±80 %.
 */
export const rentabilidadAlCierreDelAnio = (
	entrada: Omit<EntradaSerieDelAnio, 'actual'>
): Rentabilidad | null => {
	const delAnio = rentabilidadesDelAnio(entrada);
	return delAnio.length ? delAnio[delAnio.length - 1].rentabilidad : null;
};

const RESCATE = ContributionFromEnum.CANCELLMENT_PARTICIPANT_SAVINGS;
const TRASPASOS_EXTERNOS = [
	ContributionFromEnum.TRANSFER_EXTERNAL_INPUT,
	ContributionFromEnum.TRANSFER_EXTERNAL_OUTPUT
];
/**
 * Rebalanceos entre productos del mismo plan: no entra ni sale dinero. Caser los manda en pareja,
 * a veces en días distintos, y contados darían saltos en la base. Sin ellos, lo aportado según los
 * movimientos cuadra con el de Caser que usa la card (PROD, 21/10/26: 535 de 535 planes al 1 %).
 */
const INTERNOS = [
	ContributionFromEnum.TRANSFER_INTERNAL_INPUT,
	ContributionFromEnum.TRANSFER_INTERNAL_OUTPUT,
	ContributionFromEnum.CANCELLMENT_INTERNAL_INPUT,
	ContributionFromEnum.CANCELLMENT_INTERNAL_OUTPUT
];

/** Lo que entró y lo que salió en `movimientos`, separado como lo separa la card. */
const cuentas = (movimientos: SingleUserContributionData[]) => {
	const conSentido = (m: SingleUserContributionData) =>
		SENTIDO_DEL_FLUJO[m.typeContribution] * m.contributionAmount;
	return {
		// Aportaciones, con sus correcciones (que netean).
		aportado: sumBy(
			movimientos.filter(
				(m) =>
					m.typeContribution !== RESCATE &&
					!TRASPASOS_EXTERNOS.includes(m.typeContribution) &&
					!INTERNOS.includes(m.typeContribution)
			),
			conSentido
		),
		traspasado: sumBy(
			movimientos.filter((m) => TRASPASOS_EXTERNOS.includes(m.typeContribution)),
			conSentido
		),
		rescatado: sumBy(
			movimientos.filter((m) => m.typeContribution === RESCATE),
			(m) => m.contributionAmount
		)
	};
};

const porcentajeCreible = (ganancia: number, base: number): number | null => {
	if (base <= 0) return null;
	const porcentaje = (ganancia / base) * 100;
	return Math.abs(porcentaje) > MAXIMO_CREIBLE ? null : porcentaje;
};

export interface EntradaSerieDesdeElInicio {
	puntos: PlanValuePointI[];
	movimientos: SingleUserContributionData[];
	hoy: Date;
	/** La acumulada de la card (`rentabilidad`): manda en el mes en curso. */
	actual?: Rentabilidad | null;
}

export interface SerieDesdeElInicio {
	puntos: PuntoEvolucion[];
	/**
	 * `AAAA-MM` de la primera foto cuando el plan ya tenía dinero antes: la serie empieza ahí, con
	 * ese valor como base, y la gráfica lo dice. `null` si empieza en el primer euro.
	 */
	desde: string | null;
}

/**
 * La rentabilidad acumulada hasta cada mes, simple sobre lo metido (product#47, como Caser y como
 * la card): (valor + lo salido − lo entrado) / lo entrado.
 *
 * - «Entrado» son las aportaciones (con sus correcciones, sin los rebalanceos internos) y los traspasos de entrada por el
 *   importe que llegó; un traspaso de salida los reduce, sin bajar de 0, como en la card. «Salido»
 *   son los rescates: un rescate no es pérdida.
 * - Empieza el mes del primer euro, sea aportación o traspaso: antes no hay base.
 * - Con dinero antes de la primera foto (movimientos anteriores a su mes), la serie empieza en
 *   esa foto con su valor como base, y desde ahí cuenta lo que entra y sale (Marisa, 21/10/2026).
 *   Entonces no es la acumulada de la card, que va desde el primer euro.
 * - Fuera de ±80 %, hueco, como en la card.
 */
export function serieDesdeElInicio({
	puntos,
	movimientos,
	hoy,
	actual
}: EntradaSerieDesdeElInicio): SerieDesdeElInicio {
	if (puntos.length === 0) return { puntos: [], desde: null };
	const validos = confirmados(movimientos);
	const primera = puntos[0];
	const anclada = validos.some((m) => dia(m) < `${primera.month}-01`);

	const serie = puntos.flatMap((p) => {
		const valor = p.vestedRightsCents / 100;
		const porcentaje = anclada
			? (() => {
					const { aportado, traspasado, rescatado } = cuentas(
						validos.filter((m) => dia(m) > primera.date && dia(m) <= p.date)
					);
					const base = primera.vestedRightsCents / 100 + aportado + traspasado;
					return porcentajeCreible(valor + rescatado - base, base);
				})()
			: (() => {
					const { aportado, traspasado, rescatado } = cuentas(
						validos.filter((m) => dia(m) <= p.date)
					);
					const base = aportado + Math.max(traspasado, 0);
					return porcentajeCreible(valor + rescatado - base, base);
				})();
		return porcentaje === null ? [] : [{ mes: p.month, valor: porcentaje }];
	});

	if (anclada) return { puntos: serie, desde: primera.month };
	return { puntos: conLaCard(serie, mesDeMadrid(hoy), actual), desde: null };
}

/** Los años con algún punto, de todos los planes, del más reciente al más antiguo. */
export const aniosConDatos = (historiales: PlanValueHistoryI[]): string[] =>
	sortBy(uniq(historiales.flatMap((h) => h.points.map((p) => p.month.slice(0, 4))))).reverse();

/** Una fila de la gráfica: el mes y, por cada plan con punto ese mes, su valor. */
export type FilaEvolucion = { mes: string } & Record<string, string | number>;

/** Meses desde el año 0: para contar y sumar meses sin `Date` ni zonas horarias. */
const enMeses = (mes: string) => {
	const [anio, numero] = mes.split('-').map(Number);
	return anio * 12 + numero - 1;
};
const desdeMeses = (total: number) =>
	`${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;

/**
 * Las filas de todos los meses entre el primero y el último, con los que faltan vacíos: así cada
 * mes ocupa su sitio en el eje aunque nadie tenga foto. La línea cruza el que falta uniendo los de
 * los lados (decisión de Marisa, 21/10/2026: un hueco quedaba raro), pero su valor no se inventa:
 * el tooltip solo enseña meses con dato.
 */
export const conMesesSeguidos = (filas: FilaEvolucion[]): FilaEvolucion[] => {
	if (filas.length === 0) return [];
	const porMes = new Map(filas.map((fila) => [fila.mes, fila]));
	const primero = enMeses(filas[0].mes);
	return range(primero, enMeses(filas[filas.length - 1].mes) + 1)
		.map(desdeMeses)
		.map((mes) => porMes.get(mes) ?? ({ mes } as FilaEvolucion));
};

/**
 * El único dato de un plan: hay que pintarlo como punto, porque una línea de uno no se ve. Con dos
 * o más, la línea los une aunque haya meses sin foto entre medias (`connectNulls`).
 */
export const esPuntoUnico = (filas: Record<string, unknown>[], indice: number, clave: string) =>
	filas[indice]?.[clave] !== undefined &&
	filas.filter((fila) => fila[clave] !== undefined).length === 1;

/**
 * La partida de la card (`vestedRightsAtYearStartCents` y `vestedRightsAtYearStartDate` de
 * `GET /users/plans`), en euros, para `serieDelAnio`. 🔴 Sin saldo es «no se sabe» (`null`), nunca
 * 0. Con un comparsa anterior, sin los campos, `undefined`: manda la foto de diciembre.
 */
export const partidaDelPlan = (plan: {
	vestedRightsAtYearStartCents?: number | null;
	vestedRightsAtYearStartDate?: string | null;
}): { saldo: number | null; fecha: string | null } | undefined => {
	if (plan.vestedRightsAtYearStartCents === undefined) return undefined;
	return {
		saldo:
			plan.vestedRightsAtYearStartCents === null ? null : plan.vestedRightsAtYearStartCents / 100,
		fecha: plan.vestedRightsAtYearStartDate ?? null
	};
};
